import { ANALYTICS } from '@/constants/analytics/analytics';
import { Group, GroupItem, EGroupItemType, EGroupType } from '@/models';

export const BLOCK_MODE = {
  BLACK_LIST: 'blackList',
  WHITE_LIST: 'whiteList',
} as const;
export type BlockMode = (typeof BLOCK_MODE)[keyof typeof BLOCK_MODE];

interface AddItemsPayloadOptions {
  readonly screen: string;
  readonly source: string;
  readonly group: Group;
  readonly addedItems: GroupItem[];
  readonly blockMode?: BlockMode;
}

const getItemValuesByType = (
  items: GroupItem[],
  type: EGroupItemType
): string[] =>
  items.filter(item => item.itemType === type).map(item => item.itemValue);

const getFeaturesAdded = (items: GroupItem[]): string[] => {
  const features: string[] = [];
  const hasType = (type: EGroupItemType): boolean =>
    items.some(item => item.itemType === type);

  if (hasType(EGroupItemType.website)) {
    features.push(ANALYTICS.VALUES.BLOCK_SITES);
  }
  if (hasType(EGroupItemType.category)) {
    features.push(ANALYTICS.VALUES.BLOCK_CATEGORIES);
  }
  if (hasType(EGroupItemType.keyword)) {
    features.push(ANALYTICS.VALUES.BLOCK_KEYWORDS);
  }

  return features;
};

export const getBlockType = (groupType: EGroupType): string[] => {
  const blockTypeMap: Record<EGroupType, string> = {
    [EGroupType.schedule]: ANALYTICS.VALUES.SCHEDULE,
    [EGroupType.focusTime]: ANALYTICS.VALUES.FOCUS_MODE,
    [EGroupType.usageLimit]: ANALYTICS.VALUES.TIME_USAGE,
  };
  return [blockTypeMap[groupType]];
};

export const getBlockMode = (mode: BlockMode): string =>
  mode === BLOCK_MODE.BLACK_LIST
    ? ANALYTICS.VALUES.BLACK_LIST
    : ANALYTICS.VALUES.WHITE_LIST;

export const buildAddedItemsProperties = (
  addedItems: GroupItem[]
): Record<string, unknown> => {
  const sitesAdded = getItemValuesByType(addedItems, EGroupItemType.website);
  const categoriesAdded = getItemValuesByType(
    addedItems,
    EGroupItemType.category
  );
  const keywordsAdded = getItemValuesByType(addedItems, EGroupItemType.keyword);

  return {
    [ANALYTICS.PROPERTIES.ITEMS_ADDED]: addedItems.length,
    [ANALYTICS.PROPERTIES.NO_SITES_ADDED]: sitesAdded.length,
    [ANALYTICS.PROPERTIES.NO_CATEGORIES_ADDED]: categoriesAdded.length,
    [ANALYTICS.PROPERTIES.NO_KEYWORDS_ADDED]: keywordsAdded.length,
    [ANALYTICS.PROPERTIES.SITES_ADDED]: sitesAdded,
    [ANALYTICS.PROPERTIES.CATEGORIES_ADDED]: categoriesAdded,
    [ANALYTICS.PROPERTIES.KEYWORDS_ADDED]: keywordsAdded,
    [ANALYTICS.PROPERTIES.FEATURES_ADDED]: getFeaturesAdded(addedItems),
  };
};

export const createAddItemsToBlockListPayload = (
  options: AddItemsPayloadOptions
): Record<string, unknown> => {
  const {
    screen,
    source,
    group,
    addedItems,
    blockMode = BLOCK_MODE.BLACK_LIST,
  } = options;

  return {
    [ANALYTICS.PROPERTIES.SCREEN]: screen,
    [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.CLICK,
    [ANALYTICS.PROPERTIES.SOURCE]: source,
    ...buildAddedItemsProperties(addedItems),
    [ANALYTICS.PROPERTIES.BLOCK_MODE]: getBlockMode(blockMode),
    [ANALYTICS.PROPERTIES.BLOCK_TYPE]: getBlockType(group.type),
  };
};
